"use client";

import { createContext, memo, useContext, useEffect, useRef } from "react";
import { usePdfViewer, setViewerDocument, type PdfViewerInstance } from "./use-pdf-viewer";
import { usePdfDocumentStore } from "./pdfDocumentStore";
import { Spinner } from "@/components/ui/spinner";
import { PdfPageSkeleton } from "./PdfPageSkeleton";
import { usePdfTextRedaction } from "./usePdfTextRedaction";

// ─── Context ─────────────────────────────────────────────────────────────────
// Expose the viewer instance to child components (toolbar, sidebar views)
// so they can navigate pages, change zoom, etc.

type PdfViewerContextValue = {
  instanceRef: React.RefObject<PdfViewerInstance | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  isReady: boolean;
};

const PdfViewerContext = createContext<PdfViewerContextValue | null>(null);

export function usePdfViewerContext() {
  const ctx = useContext(PdfViewerContext);
  if (!ctx) throw new Error("usePdfViewerContext must be used inside <PdfViewerProvider />");
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────
// Must wrap both the sidebar (PdfSidebarViews) and the canvas (PdfViewerCore)
// so that sidebar components can access the viewer instance via context.

export function PdfViewerProvider({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { instanceRef, isReady } = usePdfViewer(containerRef);

  // Hide configured text spans from the rendered PDF text layer.
  usePdfTextRedaction(instanceRef, containerRef, isReady);

  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);
  const documents = usePdfDocumentStore((s) => s.documents);
  const pendingNavigations = usePdfDocumentStore((s) => s.pendingNavigations);
  const citationSnippets = usePdfDocumentStore((s) => s.citationSnippets);
  const activeCitationSnippet = usePdfDocumentStore((s) => s.activeCitationSnippet);
  const citationNavTick = usePdfDocumentStore((s) => s.citationNavTick);

  // Track whether Effect 1 (setViewerDocument) ran in this render cycle.
  // When it did, pdf.js is re-rendering pages and we must wait for "pagerendered".
  // When it didn't, the document is already rendered and we can navigate immediately.
  const documentSetRef = useRef(false);

  useEffect(() => {
    if (!isReady) return;
    documentSetRef.current = true;
    const doc = documents.find((d) => d.id === activeDocumentId);
    setViewerDocument(instanceRef.current, doc?.pdfDocumentProxy ?? null);
    // Snippet defaulting is handled atomically inside setActiveDocument /
    // closeDocument — no need to touch activeCitationSnippet here.
  }, [isReady, activeDocumentId, documents, instanceRef]);

  // Apply pending navigation (page + snippet highlight) after a document finishes rendering.
  useEffect(() => {
    const viewerWasSet = documentSetRef.current;
    documentSetRef.current = false;

    if (!isReady || !activeDocumentId) return;
    const instance = instanceRef.current;
    if (!instance) return;

    const doc = documents.find((d) => d.id === activeDocumentId);
    if (!doc || doc.loading) return;

    const nav = usePdfDocumentStore.getState().consumePendingNavigation(activeDocumentId);
    if (!nav) return;

    const applyNavigation = () => {
      if (nav.page && nav.page > 0) {
        instance.viewer.currentPageNumber = nav.page;
      }
      // Snippet highlighting is handled by the citationSnippets effect below.
    };

    if (viewerWasSet) {
      // New document was just set on the viewer — wait for first page render.
      const onPageRendered = () => {
        instance.eventBus.off("pagerendered", onPageRendered);
        applyNavigation();
      };
      instance.eventBus.on("pagerendered", onPageRendered);
      return () => {
        instance.eventBus.off("pagerendered", onPageRendered);
      };
    } else {
      // Document is already rendered (e.g. same active doc, only navigation changed).
      // Navigate immediately.
      applyNavigation();
    }
  }, [isReady, activeDocumentId, documents, pendingNavigations, instanceRef]);

  // Effect 3a: Scroll to the page of the active citation when it changes.
  useEffect(() => {
    if (!isReady || !activeDocumentId || !activeCitationSnippet) return;
    const instance = instanceRef.current;
    if (!instance) return;

    const citations = usePdfDocumentStore.getState().citationSnippets[activeDocumentId] || [];
    const citation = citations.find((c) => c.snippet === activeCitationSnippet);
    if (!citation?.page || citation.page <= 0) return;

    const targetPage = citation.page;
    const scrollToPage = (): boolean => {
      if (targetPage <= instance.viewer.pagesCount) {
        try {
          instance.viewer.currentPageNumber = targetPage;
          console.debug("[pdf-viewer] Effect 3a: scrolled to page", targetPage);
          return true;
        } catch { /* viewer not ready */ }
      }
      return false;
    };

    // Try immediately — works when the document is already rendered.
    if (scrollToPage()) return;

    // Viewer not ready (doc switch in progress) — wait for first page render.
    console.debug("[pdf-viewer] Effect 3a: viewer not ready, waiting for pagerendered to scroll to page", targetPage);
    const onPageRendered = () => {
      instance.eventBus.off("pagerendered", onPageRendered);
      scrollToPage();
    };
    instance.eventBus.on("pagerendered", onPageRendered);
    return () => {
      instance.eventBus.off("pagerendered", onPageRendered);
    };
  }, [isReady, activeDocumentId, activeCitationSnippet, citationNavTick, instanceRef]);

  // Effect 3b: Citation highlights via CSS Highlight API.
  // Listens for `textlayerrendered` (not `pagerendered`) so text nodes are
  // guaranteed to exist when we scan.  Re-scans on every text-layer render
  // so highlights survive scale changes, scrolling, and page-fit adjustments.
  // Uses two highlight registers: one for inactive citations, one for the
  // currently active citation so they can be styled differently.
  useEffect(() => {
    if (!isReady || !activeDocumentId) return;
    const instance = instanceRef.current;
    const container = containerRef.current;
    if (!instance || !container) return;

    // Generated docs never have citations — short-circuit so stale snippets
    // (preserved through a citation→generated mode-switch in the store) don't
    // attempt to render over an unrelated document. Defense-in-depth pairs
    // with the store's mode-switch wipe of activeCitationSnippet.
    const activeDoc = documents.find((d) => d.id === activeDocumentId);
    if (activeDoc?.kind === "generated") {
      clearCitationHighlights();
      return;
    }

    const citations = citationSnippets[activeDocumentId] || [];
    if (citations.length === 0) {
      clearCitationHighlights();
      return;
    }

    // Only highlight the active citation snippet (user-clicked).
    const activeSnippets = activeCitationSnippet
      ? citations.filter((c) => c.snippet === activeCitationSnippet).map((c) => c.snippet)
      : [];
    const normalizedSnippets = normalizeSnippets(activeSnippets);

    // Expected page from the citation metadata (the page the LLM claimed).
    const activeCitation = activeCitationSnippet
      ? citations.find((c) => c.snippet === activeCitationSnippet)
      : undefined;
    const expectedPage = activeCitation?.page;

    // Prevent repeated adjacent-page redirects within the same effect cycle.
    let hasRedirected = false;

    const scanAllRenderedPages = () => {
      const activeRanges: Range[] = [];
      const matchedPages = new Set<number>();
      let pagesScanned = 0;
      for (const pageEl of container.querySelectorAll(".page[data-page-number]")) {
        const textLayer = pageEl.querySelector(".textLayer");
        if (!textLayer || textLayer.childNodes.length === 0) continue;
        pagesScanned++;
        const pageNum = parseInt(pageEl.getAttribute("data-page-number")!, 10);
        const pageRanges = findHighlightRanges(textLayer, normalizedSnippets);
        if (pageRanges.length > 0) matchedPages.add(pageNum);
        activeRanges.push(...pageRanges);
      }

      // If snippet not found on the LLM-stated page but found on an adjacent
      // page, navigate there — the model picked the closest page but was off by one.
      if (
        !hasRedirected &&
        expectedPage &&
        activeRanges.length > 0 &&
        !matchedPages.has(expectedPage)
      ) {
        const adjacentPage = matchedPages.has(expectedPage - 1)
          ? expectedPage - 1
          : matchedPages.has(expectedPage + 1)
            ? expectedPage + 1
            : undefined;
        if (adjacentPage) {
          hasRedirected = true;
          console.debug(
            "[pdf-viewer] scanAllRenderedPages: snippet not on expected page",
            expectedPage,
            "— redirecting to adjacent page",
            adjacentPage,
          );
          instance.viewer.currentPageNumber = adjacentPage;
        }
      }

      console.debug("[pdf-viewer] scanAllRenderedPages: scanned", pagesScanned, "pages | active ranges:", activeRanges.length);

      if (activeRanges.length === 0 && normalizedSnippets.length > 0 && pagesScanned > 0) {
        console.warn(
          "[pdf-viewer] Citation highlight failed — snippet not found in PDF text.",
          { docId: activeDocumentId, expectedPage, snippetLength: normalizedSnippets[0]?.length, pagesScanned },
        );
      }

      const hl = (CSS as { highlights: Map<string, Highlight> }).highlights;
      ensureHighlightStyle();
      if (activeRanges.length > 0) {
        hl.set("citation-highlight-active", new Highlight(...activeRanges));
      } else {
        hl.delete("citation-highlight-active");
      }
    };

    // Scan immediately for any text layers already populated.
    scanAllRenderedPages();

    // Re-scan whenever a text layer finishes rendering (scroll, zoom,
    // scale change, initial load).  Debounced via rAF so multiple pages
    // rendering in the same frame only trigger one scan.
    let rafId = 0;
    const onTextLayerRendered = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(scanAllRenderedPages);
    };
    instance.eventBus.on("textlayerrendered", onTextLayerRendered);

    return () => {
      cancelAnimationFrame(rafId);
      instance.eventBus.off("textlayerrendered", onTextLayerRendered);
      clearCitationHighlights();
    };
  }, [isReady, activeDocumentId, citationSnippets, activeCitationSnippet, documents, instanceRef, containerRef]);

  return (
    <PdfViewerContext.Provider value={{ instanceRef, containerRef, isReady }}>
      {children}
    </PdfViewerContext.Provider>
  );
}

// ─── Inject ::highlight CSS at runtime ────────────────────────────────────────
// PostCSS/Lightning CSS does not recognise the CSS Custom Highlight API
// pseudo-element, so we inject the rule from JS once.

let highlightStyleInjected = false;
function ensureHighlightStyle() {
  if (highlightStyleInjected) return;
  if (typeof document === "undefined") return;
  highlightStyleInjected = true;
  const style = document.createElement("style");
  style.textContent =
    ".pdf-viewer-container .textLayer ::highlight(citation-highlight-active) { background-color: oklch(0.82 0.18 80 / 0.65); }";
  document.head.appendChild(style);
}

// ─── Citation highlight helpers ───────────────────────────────────────────────

function clearCitationHighlights() {
  if (typeof CSS !== "undefined" && "highlights" in CSS) {
    const hl = (CSS as { highlights: Map<string, unknown> }).highlights;
    hl.delete("citation-highlight-active");
  }
}

/**
 * Normalize a string for fuzzy matching: strip diacritics, strip ALL whitespace,
 * strip invisible characters, and lowercase.
 *
 * Whitespace is stripped entirely (not collapsed) so that matches succeed even
 * when pdf.js text-layer spans lack inter-span spaces at line-break boundaries.
 * Citation snippets are long enough that false positives from space removal are
 * negligible in practice.
 *
 * Returns the normalized string and a position map where `map[i]` is the index
 * in the **original raw string** that produced normalized character `i`.
 */
function normalizeForMatch(raw: string): { text: string; map: number[] } {
  const map: number[] = [];
  const chars: string[] = [];

  for (let i = 0; i < raw.length; i++) {
    const code = raw.charCodeAt(i);

    // Skip combining diacritical marks already present in the raw string (NFD text).
    if (code >= 0x0300 && code <= 0x036f) continue;

    // Skip zero-width / invisible characters.
    if (
      code === 0x200b || code === 0x200c || code === 0x200d || // ZW space/joiner
      code === 0xfeff || // BOM / ZW no-break space
      code === 0x00ad || // soft hyphen
      code === 0x200e || code === 0x200f    // bidi marks
    ) continue;

    // Strip ALL whitespace (including non-breaking space U+00A0).
    // pdf.js text-layer spans often lack whitespace at line-break boundaries,
    // so stripping spaces from both sides makes matching boundary-agnostic.
    if (/\s/.test(raw[i]) || code === 0x00a0) continue;

    // NFD-decompose just this character, keep only base character(s).
    const nfd = raw[i].normalize("NFD");
    for (let j = 0; j < nfd.length; j++) {
      const cp = nfd.charCodeAt(j);
      if (cp >= 0x0300 && cp <= 0x036f) continue; // skip combining marks from decomposition
      chars.push(nfd[j].toLowerCase());
      map.push(i); // always points back to the position in the original raw string
    }
  }

  return { text: chars.join(""), map };
}

/** Minimum normalized snippet length to avoid false-positive highlights. */
const MIN_SNIPPET_LENGTH = 10;

/** Normalize snippet strings for exact matching against PDF text layers. */
function normalizeSnippets(snippets: string[]): string[] {
  return snippets
    .map((s) => normalizeForMatch(s).text)
    .filter((s) => s.length >= MIN_SNIPPET_LENGTH);
}

// ─── Trigram fuzzy matching ───────────────────────────────────────────────────

/** Minimum trigram similarity (Dice coefficient) to accept a fuzzy match. */
const TRIGRAM_MIN_SIMILARITY = 0.55;

/** Extract distinct character trigrams from a string. */
function computeTrigrams(s: string): Set<string> {
  const t = new Set<string>();
  for (let i = 0; i <= s.length - 3; i++) {
    t.add(s.slice(i, i + 3));
  }
  return t;
}

/**
 * Sliding-window trigram search: find the best approximate match for
 * `normSnippet` inside `normText` using Dice coefficient on character
 * trigram sets. Tries windows at 90%, 100%, and 110% of snippet length
 * to tolerate minor length differences (word additions/removals by LLM).
 *
 * The window slides incrementally — trigram counts are updated in O(1) per
 * step, giving O(n) per window size where n = normText.length.
 */
function findBestTrigramMatch(
  normText: string,
  normSnippet: string,
): { index: number; length: number; similarity: number } | null {
  if (normSnippet.length < 6) return null;

  const snippetTri = computeTrigrams(normSnippet);
  if (snippetTri.size === 0) return null;

  let bestIdx = -1;
  let bestLen = 0;
  let bestSim = 0;

  const windowSizes = [
    normSnippet.length,
    Math.floor(normSnippet.length * 0.9),
    Math.ceil(normSnippet.length * 1.1),
  ].filter((w) => w >= 6 && w <= normText.length);

  for (const winLen of windowSizes) {
    const winCounts = new Map<string, number>();
    let distinctCount = 0;
    let shared = 0;

    const add = (tri: string) => {
      const prev = winCounts.get(tri) || 0;
      winCounts.set(tri, prev + 1);
      if (prev === 0) {
        distinctCount++;
        if (snippetTri.has(tri)) shared++;
      }
    };

    const remove = (tri: string) => {
      const prev = winCounts.get(tri)!;
      if (prev === 1) {
        winCounts.delete(tri);
        distinctCount--;
        if (snippetTri.has(tri)) shared--;
      } else {
        winCounts.set(tri, prev - 1);
      }
    };

    // Initialize first window [0, winLen)
    for (let j = 0; j <= winLen - 3; j++) {
      add(normText.slice(j, j + 3));
    }

    let sim = distinctCount > 0
      ? (2 * shared) / (snippetTri.size + distinctCount)
      : 0;
    if (sim > bestSim) {
      bestSim = sim;
      bestIdx = 0;
      bestLen = winLen;
    }

    // Slide one character at a time
    for (let i = 1; i <= normText.length - winLen; i++) {
      remove(normText.slice(i - 1, i + 2));
      add(normText.slice(i + winLen - 3, i + winLen));

      sim = distinctCount > 0
        ? (2 * shared) / (snippetTri.size + distinctCount)
        : 0;
      if (sim > bestSim) {
        bestSim = sim;
        bestIdx = i;
        bestLen = winLen;
      }
    }
  }

  if (bestIdx >= 0 && bestSim >= TRIGRAM_MIN_SIMILARITY) {
    return { index: bestIdx, length: bestLen, similarity: bestSim };
  }
  return null;
}

/**
 * Scan a single page's text layer and return CSS Highlight API Range objects
 * for every snippet match found.
 */
function findHighlightRanges(
  pageTextLayer: Element,
  normSnippets: string[],
): Range[] {
  const textNodes: { node: Text; start: number; length: number }[] = [];
  let fullText = "";

  const walker = document.createTreeWalker(pageTextLayer, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const t = node.textContent || "";
    textNodes.push({ node: node as Text, start: fullText.length, length: t.length });
    fullText += t;
  }

  if (fullText.length === 0) return [];

  const { text: normText, map: normMap } = normalizeForMatch(fullText);
  const ranges: Range[] = [];

  /** Build a Range from normalized match positions. Returns null on failure. */
  const buildRange = (matchIdx: number, matchLength: number): Range | null => {
    const matchEndNorm = matchIdx + matchLength;

    // Map normalized positions back to raw fullText positions.
    const rawStart = normMap[matchIdx];
    const lastRawIdx = normMap[matchEndNorm - 1];
    let rawEnd = lastRawIdx + 1;
    while (rawEnd < fullText.length && /[\u0300-\u036f]/.test(fullText[rawEnd])) {
      rawEnd++;
    }

    // Map raw positions to text node + offset.
    let startNodeIdx = -1;
    let startOffset = 0;
    let endNodeIdx = -1;
    let endOffset = 0;

    for (let i = 0; i < textNodes.length; i++) {
      const tn = textNodes[i];
      const nodeEnd = tn.start + tn.length;

      if (startNodeIdx === -1 && rawStart >= tn.start && rawStart < nodeEnd) {
        startNodeIdx = i;
        startOffset = rawStart - tn.start;
      }

      if (rawEnd > tn.start && rawEnd <= nodeEnd) {
        endNodeIdx = i;
        endOffset = rawEnd - tn.start;
        break;
      }
    }

    if (startNodeIdx === -1 || endNodeIdx === -1) return null;
    try {
      const range = document.createRange();
      range.setStart(textNodes[startNodeIdx].node, startOffset);
      range.setEnd(textNodes[endNodeIdx].node, endOffset);
      return range;
    } catch {
      return null;
    }
  };

  for (const normSnippet of normSnippets) {
    let searchPos = 0;
    let found = false;

    while (true) {
      const matchIdx = normText.indexOf(normSnippet, searchPos);
      if (matchIdx === -1) break;
      found = true;

      const range = buildRange(matchIdx, normSnippet.length);
      if (range) ranges.push(range);

      searchPos = matchIdx + normSnippet.length;
    }

    // Fallback: if no exact match, try progressively shorter prefixes.
    // The LLM may produce a snippet that bleeds past the actual sentence boundary.
    if (!found) {
      const minPrefix = Math.max(MIN_SNIPPET_LENGTH, Math.floor(normSnippet.length * 0.4));
      for (let len = normSnippet.length - 1; len >= minPrefix; len--) {
        const prefix = normSnippet.slice(0, len);
        const matchIdx = normText.indexOf(prefix);
        if (matchIdx !== -1) {
          const range = buildRange(matchIdx, len);
          if (range) {
            ranges.push(range);
            found = true;
            console.debug("[pdf-viewer] findHighlightRanges: prefix match for snippet:", normSnippet.slice(0, 40), `(${len}/${normSnippet.length} chars)`);
          }
          break;
        }
      }
    }

    // Fallback 2: trigram sliding-window fuzzy match.
    // Catches OCR single-char errors, minor LLM rephrasing, and
    // middle-of-snippet matches that prefix fallback cannot find.
    if (!found) {
      const trigramMatch = findBestTrigramMatch(normText, normSnippet);
      if (trigramMatch) {
        const range = buildRange(trigramMatch.index, trigramMatch.length);
        if (range) {
          ranges.push(range);
          found = true;
          if (import.meta.env.DEV) {
            console.debug(
              "[pdf-viewer] findHighlightRanges: trigram match",
              `(similarity: ${(trigramMatch.similarity * 100).toFixed(1)}%, window: ${trigramMatch.length}/${normSnippet.length})`,
            );
          }
        }
      }
    }

    if (found) {
      if (ranges.length > 0 && import.meta.env.DEV) {
        console.debug("[pdf-viewer] findHighlightRanges: match (length:", normSnippet.length, ")");
      }
    } else {
      console.warn("[pdf-viewer] findHighlightRanges: no match (length:", normSnippet.length, ")");
    }
  }

  return ranges;
}

// ─── Canvas component ────────────────────────────────────────────────────────
// Renders the actual pdf.js viewer div; must be inside <PdfViewerProvider />.

export const PdfViewerCore = memo(function PdfViewerCore() {
  const { containerRef } = usePdfViewerContext();
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);
  const documents = usePdfDocumentStore((s) => s.documents);
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const isLoading = activeDoc?.loading === true;

  return (
    <div className="relative flex-1 bg-muted/40">
      <div
        ref={containerRef}
        className="pdf-viewer-container absolute inset-0 overflow-auto"
      >
        <div className="pdfViewer" />
      </div>
      {isLoading && <PdfPageSkeleton />}
    </div>
  );
});
